import React, { forwardRef, useCallback, useLayoutEffect, useState } from "react";
import BasicHeader from "pages/MaterialSpecs/components/BasicHeader/BasicHeader";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { useDispatch, useSelector } from "react-redux";
import {
    getMaterialDetailsById,
    getMaterialSpecById,
    updateExistingSpecProperties,
} from "store/features/materialSpecs/specs";
import { Box, CircularProgress, Divider, FormControl, Tooltip } from "@material-ui/core";
import { useMaterialSpecAssets } from "hooks/api/useMaterialSpecAssets";
import {
    Dropzone,
    DropzoneBox,
    TextField,
    BoxContainer,
    PermissionWrapper,
    ButtonContainer,
    ApproveButton,
    SecondaryButton,
} from "components";
import { useTranslation } from "react-i18next";
import EmptyListPlaceholder from "components/EmptyListPlaceholder/EmptyListPlaceholder";
import { useQueryCache } from "react-query";
import { LoadingOverlay } from "components/LoadingOverlay/LoadingOverlay";
import { AssetDetails } from "pages/MaterialSpecs/components/InfoPanel/Assets/AssetDetails/AssetDetails";
import { usePermissions } from "hooks";
import { useUploadAsset, useAssetDelete, useFormFields, useDownloadAsset } from "hooks/api";
import { API } from "api";
import { useSubmitAsset } from "hooks/api/useSubmitAsset";

// TODO: fix interfaces
// eslint-disable-next-line
const Assets = (props: any, ref: any) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const queryClient = useQueryCache();
    const {
        selectedMaterialSpecId,
        selectedMaterialId,
        setPreview,
        setPreviewVisibility,
        switchVisual,
        isVisualVisible,
        isPrinted,
    } = useMaterialSpecs();
    const materialSpec = useSelector(getMaterialSpecById(selectedMaterialSpecId));
    const materialDetails = useSelector(getMaterialDetailsById(selectedMaterialSpecId, selectedMaterialId));
    const { SENDTOPRODUCTION } = usePermissions([
        ...(materialSpec?.permissions || []),
        ...(materialDetails?.permissions || []),
    ]);
    const [isAnyAssetWorking, setAnyAssetWorking] = useState(false);
    const [text, setText] = useState(materialDetails?.description || "");
    const [handleUpdateDescription, { isLoading: isDescriptionUpdating }] = useFormFields();
    const { data, isFetching, refetch } = useMaterialSpecAssets({
        isPrinted,
        materialId: isPrinted ? undefined : selectedMaterialId,
        materialSpecId: selectedMaterialSpecId,
    });
    // matters for useEffect deps to work correctly
    const assets = data || [];
    const [handleDownloadAsset, { isLoading: isDownloading }] = useDownloadAsset();

    const [handleUploadAsset] = useUploadAsset("DEFAULT", async (uploadedAssets) => {
        queryClient.setQueryData(
            ["assets", isPrinted ? selectedMaterialSpecId : selectedMaterialId],
            [...uploadedAssets, ...assets]
        );
        const isAnyAssetWorking = uploadedAssets.some(({ preview }) => !preview);

        if (isAnyAssetWorking) {
            const checkAssetsCompleteStatus = async (retries = 0) => {
                const newAssets = await refetch();

                if (newAssets) {
                    const isEveyAssetCompleted = newAssets.every(({ preview }) => !!preview);

                    if (isEveyAssetCompleted) {
                        queryClient.setQueryData(
                            ["assets", isPrinted ? selectedMaterialSpecId : selectedMaterialId],
                            newAssets
                        );
                        setAnyAssetWorking(false);

                        return;
                    }

                    setTimeout(() => checkAssetsCompleteStatus(retries + 1), 1000 * retries * 1.2);
                }
            };

            await checkAssetsCompleteStatus();
        }

        setAnyAssetWorking(false);
    });
    const [handleDeleteAsset, { isLoading: isDeleting }] = useAssetDelete((deletedAssetId) => {
        queryClient.setQueryData(
            ["assets", isPrinted ? selectedMaterialSpecId : selectedMaterialId],
            assets?.filter(({ id }) => id !== deletedAssetId)
        );
    });
    const [handleSubmitAsset, { isLoading: isAssetSubmitting }] = useSubmitAsset();
    const visualAsset = assets.find((asset) => asset.type === "VISUAL");
    const otherAssets = assets.filter(({ type }) => type !== "VISUAL");

    useLayoutEffect(() => {
        if (assets.length !== 0 && ref.current !== null) {
            ref.current.scrollIntoView({ behavior: "auto", block: "start" });
        }
    }, [assets.length, ref]);

    // TODO: Add function that will navigate to Preview => Visual
    const handleVisualPreview = useCallback(() => {
        if (visualAsset) {
            setPreview(
                `${API.serverUrl}${visualAsset.bigPreview?.url}`,
                visualAsset.bigPreview?.format || "",
                selectedMaterialSpecId,
                selectedMaterialId,
                visualAsset.id
            );
        }

        setPreviewVisibility(true);
        switchVisual(true);
    }, [setPreview, visualAsset, selectedMaterialId, selectedMaterialSpecId, setPreviewVisibility, switchVisual]);

    const handleDescriptionUpdate = useCallback(
        async (isComplete = false) => {
            if (selectedMaterialSpecId && selectedMaterialId) {
                await handleUpdateDescription({
                    materialSpecId: selectedMaterialSpecId,
                    materialId: isPrinted ? undefined : selectedMaterialId,
                    fields: { MaterialSpecification__visualdescription: text },
                });
                await handleSubmitAsset({ materialSpecId: selectedMaterialSpecId, complete: isComplete });

                if (isComplete) {
                    dispatch(updateExistingSpecProperties.request({ materialSpecId: selectedMaterialSpecId }));
                }
            }
        },
        [
            handleSubmitAsset,
            handleUpdateDescription,
            selectedMaterialId,
            selectedMaterialSpecId,
            text,
            dispatch,
            isPrinted,
        ]
    );

    const handleDropAssets = useCallback(
        async (files: File[]) => {
            if (selectedMaterialSpecId && selectedMaterialId) {
                setAnyAssetWorking(true);
                await handleUploadAsset({
                    files,
                    materialSpecId: selectedMaterialSpecId,
                    materialId: !isPrinted ? selectedMaterialId : undefined,
                });
            }
        },
        [selectedMaterialSpecId, selectedMaterialId, handleUploadAsset, isPrinted]
    );

    if (!materialDetails || !selectedMaterialSpecId || !selectedMaterialId) {
        return null;
    }

    return (
        <LoadingOverlay active={isAnyAssetWorking || isDeleting || isDescriptionUpdating}>
            <BasicHeader
                bookedDimensions={materialDetails.bookedSize}
                dimensions={materialDetails.dimensions}
                modificationDate={materialDetails.modificationDate}
            />

            <BoxContainer title={t("campaign.assets.label.notesAndVisual")}>
                <FormControl fullWidth={true}>
                    <TextField
                        value={text}
                        variant="outlined"
                        backgroundColor="white"
                        multiline
                        rows={3}
                        label={t("campaign.assets.label.notes")}
                        onChange={(e) => setText(e.target.value)}
                    />
                </FormControl>
                <PermissionWrapper name="SENDTOPRODUCTION" permissions={materialSpec?.permissions}>
                    {({ disabled, tooltip }) => (
                        <ButtonContainer display="flex" justifyContent="flex-end" flexDirection="row">
                            <Tooltip title={tooltip} interactive>
                                <span>
                                    {!isVisualVisible && (
                                        <SecondaryButton
                                            variant="outlined"
                                            color="primary"
                                            disabled={disabled}
                                            onClick={handleVisualPreview}>
                                            {visualAsset
                                                ? t("campaign.assets.label.showVisual")
                                                : t("campaign.assets.label.addVisual")}
                                        </SecondaryButton>
                                    )}
                                </span>
                            </Tooltip>
                            {visualAsset && (
                                <Tooltip title={tooltip} interactive>
                                    <span>
                                        <SecondaryButton
                                            variant="outlined"
                                            color="primary"
                                            disabled={disabled}
                                            onClick={() => handleDeleteAsset(visualAsset?.id)}>
                                            {t("campaign.assets.label.removeVisual")}
                                        </SecondaryButton>
                                    </span>
                                </Tooltip>
                            )}
                        </ButtonContainer>
                    )}
                </PermissionWrapper>
            </BoxContainer>

            <BoxContainer
                title={`${t("campaign.label.assignedAssets")} (${otherAssets.length.toString()})`}
                enableMargin={false}>
                {isFetching && assets.length === 0 && (
                    <Box p={4} display="flex" justifyContent="center">
                        <CircularProgress color="primary" size={40} />
                    </Box>
                )}
                {otherAssets.map((asset) => {
                    return (
                        <AssetDetails
                            {...asset}
                            key={asset.id}
                            isDeleting={isDeleting}
                            isDownloading={isDownloading}
                            onDeleteAsset={handleDeleteAsset}
                            onDownloadAsset={handleDownloadAsset}
                        />
                    );
                })}
                {!assets?.length && !isFetching && <EmptyListPlaceholder label={t("campaign.assets.label.noAssets")} />}
            </BoxContainer>

            {SENDTOPRODUCTION?.display && (
                <Box width="100%" paddingY={2} position="sticky" bottom="0" bgcolor="white" zIndex={1}>
                    <Divider />
                    <PermissionWrapper name="SENDTOPRODUCTION" permissions={materialSpec?.permissions}>
                        {({ disabled }) => (
                            <DropzoneBox
                                sizeY={350}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                marginTop={2}>
                                <Dropzone
                                    disabled={disabled || isAnyAssetWorking}
                                    label={t("campaign.label.dragAndDropArea")}
                                    onDrop={handleDropAssets}
                                />
                            </DropzoneBox>
                        )}
                    </PermissionWrapper>
                    <ButtonContainer pt={2} display="flex" justifyContent="flex-end">
                        <PermissionWrapper name="SENDTOPRODUCTION" permissions={materialSpec?.permissions}>
                            {({ disabled, tooltip }) => (
                                <>
                                    <Tooltip title={tooltip} interactive>
                                        <span>
                                            <SecondaryButton
                                                disableRipple
                                                variant="outlined"
                                                color="primary"
                                                disabled={disabled || isDescriptionUpdating || isAssetSubmitting}
                                                onClick={() => handleDescriptionUpdate()}>
                                                {t("buttons.label.submit")}
                                            </SecondaryButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={tooltip} interactive>
                                        <span>
                                            <ApproveButton
                                                disableRipple
                                                variant="outlined"
                                                color="primary"
                                                disabled={disabled || isDescriptionUpdating || isAssetSubmitting}
                                                onClick={() => handleDescriptionUpdate(true)}>
                                                {t("buttons.label.sendToProduction")}
                                            </ApproveButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </PermissionWrapper>
                    </ButtonContainer>
                </Box>
            )}
        </LoadingOverlay>
    );
};

export default forwardRef(Assets);
